import { useMemo } from 'react'

import { useCourseStore } from './courseStore'
import { useSessionStore } from './sessionStore'
import { useAttendeeStore } from './attendeeStore'
import { useCertificateStore, getCertificateStatus } from './certificateStore'
import type { ClassSessionState, CloseSessionResult, OpenCheckinPayload } from './types'

export { getCertificateStatus }

/* ─── Static combined actions (stable references) ─────────────────────── */

const loadCourses = () => useCourseStore.getState().loadCourses()

const loadSessions = async () => {
  await useCourseStore.getState().loadCourses()
  await useSessionStore.getState().loadSessions()
}

const createSession = (data: Parameters<ClassSessionState['createSession']>[0]) =>
  useSessionStore.getState().createSession(data)

const openCheckin = (sessionId: string, payload?: OpenCheckinPayload) =>
  useSessionStore.getState().openCheckin(sessionId, payload)
const beginClass = (sessionId: string) => useSessionStore.getState().beginClass(sessionId)
const closeSession = async (
  sessionId: string,
  attendeeIds?: string[]
): Promise<CloseSessionResult> => {
  const result = await useSessionStore.getState().closeSession(sessionId, attendeeIds)
  await loadCertificates()
  return result
}
const updateSessionMaxAttendees = (sessionId: string, maxAttendees: number) =>
  useSessionStore.getState().updateSessionMaxAttendees(sessionId, maxAttendees)
const updateSession = (sessionId: string, data: Parameters<ClassSessionState['updateSession']>[1]) =>
  useSessionStore.getState().updateSession(sessionId, data)

const deleteSession = async (sessionId: string) => {
  await useSessionStore.getState().deleteSession(sessionId)
}

const loadSessionAttendees = (sessionId: string) =>
  useAttendeeStore.getState().loadSessionAttendees(sessionId)

const loadMyAttendees = () => useAttendeeStore.getState().loadMyAttendees()
const loadAllAttendees = () => useAttendeeStore.getState().loadAllAttendees()

const registerStudent = (
  sessionId: string,
  studentData: Parameters<ClassSessionState['registerStudent']>[1]
) => useAttendeeStore.getState().registerStudent(sessionId, studentData)

const submitQuiz = (attendeeId: string, answers: Record<string, number>) =>
  useAttendeeStore.getState().submitQuiz(attendeeId, answers)

const updateAttendeeContact = (
  attendeeId: string,
  data: Parameters<ClassSessionState['updateAttendeeContact']>[1]
) => useAttendeeStore.getState().updateAttendeeContact(attendeeId, data)

const markAttendeeCompleted = (attendeeId: string) =>
  useAttendeeStore.getState().markAttendeeCompleted(attendeeId)

const deleteAttendee = (attendeeId: string) => useAttendeeStore.getState().deleteAttendee(attendeeId)

const handleRealtimeAttendee = (
  action: 'create' | 'update' | 'delete',
  record: Record<string, unknown>
) => useAttendeeStore.getState().handleRealtimeAttendee(action, record)

const getSessionAttendees = (sessionId: string) =>
  useAttendeeStore.getState().getSessionAttendees(sessionId)

const loadCertificates = () => useCertificateStore.getState().loadCertificates()
const generateCertificate = (attendeeId: string) =>
  useCertificateStore.getState().generateCertificate(attendeeId)
const revokeCertificate = (certificateId: string, opts: { revoked: boolean; reason?: string }) =>
  useCertificateStore.getState().revokeCertificate(certificateId, opts)
const getCertificateByToken = (token: string) =>
  useCertificateStore.getState().getCertificateByToken(token)
const getAttendeeCertificate = (attendeeId: string) =>
  useCertificateStore.getState().getAttendeeCertificate(attendeeId)

/** Stable action object – never changes reference. */
const STATIC_ACTIONS: Pick<
  ClassSessionState,
  | 'loadCourses'
  | 'loadSessions'
  | 'createSession'
  | 'openCheckin'
  | 'beginClass'
  | 'closeSession'
  | 'updateSessionMaxAttendees'
  | 'updateSession'
  | 'deleteSession'
  | 'loadSessionAttendees'
  | 'loadMyAttendees'
  | 'loadAllAttendees'
  | 'registerStudent'
  | 'submitQuiz'
  | 'updateAttendeeContact'
  | 'markAttendeeCompleted'
  | 'deleteAttendee'
  | 'handleRealtimeAttendee'
  | 'getSessionAttendees'
  | 'loadCertificates'
  | 'generateCertificate'
  | 'revokeCertificate'
  | 'getCertificateByToken'
  | 'getAttendeeCertificate'
> = {
  loadCourses,
  loadSessions,
  createSession,
  openCheckin,
  beginClass,
  closeSession,
  updateSessionMaxAttendees,
  updateSession,
  deleteSession,
  loadSessionAttendees,
  loadMyAttendees,
  loadAllAttendees,
  registerStudent,
  submitQuiz,
  updateAttendeeContact,
  markAttendeeCompleted,
  deleteAttendee,
  handleRealtimeAttendee,
  getSessionAttendees,
  loadCertificates,
  generateCertificate,
  revokeCertificate,
  getCertificateByToken,
  getAttendeeCertificate,
}

/** Build data-only snapshot from the four specialized stores. */
function buildDataState(): Pick<
  ClassSessionState,
  | 'courses'
  | 'sessions'
  | 'students'
  | 'attendees'
  | 'certificates'
  | '_certSequence'
  | 'isLoading'
  | 'sessionsLoaded'
  | 'error'
> {
  const course = useCourseStore.getState()
  const session = useSessionStore.getState()
  const attendee = useAttendeeStore.getState()
  const certificate = useCertificateStore.getState()

  return {
    courses: course.courses,
    sessions: session.sessions,
    students: attendee.students,
    attendees: attendee.attendees,
    certificates: certificate.certificates,
    _certSequence: certificate._certSequence,
    isLoading: session.isLoading,
    sessionsLoaded: session.sessionsLoaded,
    error: session.error,
  }
}

/* ─── Hook ────────────────────────────────────────────────────────────── */

export function useClassSessionStore(): ClassSessionState
export function useClassSessionStore<T>(
  selector: (state: ClassSessionState) => T
): T
export function useClassSessionStore<T>(
  selector?: (state: ClassSessionState) => T
): T | ClassSessionState {
  // Subscribe to individual properties only — never to the entire store.
  // This prevents every consumer from re-rendering when *any* store changes.
  const courses = useCourseStore((s) => s.courses)
  const sessions = useSessionStore((s) => s.sessions)
  const isLoading = useSessionStore((s) => s.isLoading)
  const sessionsLoaded = useSessionStore((s) => s.sessionsLoaded)
  const error = useSessionStore((s) => s.error)
  const students = useAttendeeStore((s) => s.students)
  const attendees = useAttendeeStore((s) => s.attendees)
  const certificates = useCertificateStore((s) => s.certificates)
  const _certSequence = useCertificateStore((s) => s._certSequence)

  const data = useMemo(
    () => ({
      courses,
      sessions,
      students,
      attendees,
      certificates,
      _certSequence,
      isLoading,
      sessionsLoaded,
      error,
    }),
    [
      courses,
      sessions,
      isLoading,
      sessionsLoaded,
      error,
      students,
      attendees,
      certificates,
      _certSequence,
    ]
  )

  const state = useMemo(
    () => ({ ...data, ...STATIC_ACTIONS }),
    [data]
  )

  if (!selector) {
    return state
  }

  return useMemo(() => selector(state), [selector, state])
}

/* ─── Static helpers for non-React consumers ──────────────────────────── */

useClassSessionStore.getState = (): ClassSessionState => ({
  ...buildDataState(),
  ...STATIC_ACTIONS,
})

useClassSessionStore.setState = (
  updater:
    | ClassSessionState
    | Partial<ClassSessionState>
    | ((state: ClassSessionState) => ClassSessionState | Partial<ClassSessionState>),
) => {
  const current = buildDataState()
  const partial =
    typeof updater === 'function'
      ? (updater as (state: ClassSessionState) => Partial<ClassSessionState>)({
          ...current,
          ...STATIC_ACTIONS,
        })
      : updater

  const p = partial as Partial<ClassSessionState>

  if (p.courses !== undefined) {
    useCourseStore.setState({ courses: p.courses } as never)
  }
  if (p.sessions !== undefined) {
    useSessionStore.setState({ sessions: p.sessions } as never)
  }
  if (p.students !== undefined) {
    useAttendeeStore.setState({ students: p.students } as never)
  }
  if (p.attendees !== undefined) {
    useAttendeeStore.setState({ attendees: p.attendees } as never)
  }
  if (p.certificates !== undefined) {
    useCertificateStore.setState({ certificates: p.certificates } as never)
  }
  if (p._certSequence !== undefined) {
    useCertificateStore.setState({ _certSequence: p._certSequence } as never)
  }
  if (p.isLoading !== undefined) {
    useSessionStore.setState({ isLoading: p.isLoading } as never)
  }
  if (p.sessionsLoaded !== undefined) {
    useSessionStore.setState({ sessionsLoaded: p.sessionsLoaded } as never)
  }
  if (p.error !== undefined) {
    useSessionStore.setState({ error: p.error } as never)
  }
}
